import React, { useState } from 'react';
import { motion } from 'framer-motion';
import api from '../services/api';
import '../styles/CheckTicketStatus.css';

interface BookingStatus {
  id: string;
  bookingCode: string;
  paymentStatus: string;
  ticketsGenerated: boolean;
  ticketsCount: number;
  ticketUrls?: Array<{
    ticketNumber: number;
    url: string;
    publicId: string;
    downloadUrl: string;
  }>;
  ticketsZipUrl?: string;
  email: string;
}

interface CloudinaryStatus {
  generated: boolean;
  uploadedCount: number;
  hasZip: boolean;
}

function CheckTicketStatus() {
  const [bookingId, setBookingId] = useState('');
  const [loading, setLoading] = useState(false);
  const [booking, setBooking] = useState<BookingStatus | null>(null);
  const [cloudinaryStatus, setCloudinaryStatus] = useState<CloudinaryStatus | null>(null);
  const [error, setError] = useState('');

  const checkStatus = async () => {
    if (!bookingId.trim()) {
      setError('الرجاء إدخال معرف الحجز');
      return;
    }

    setLoading(true);
    setError('');
    setBooking(null);

    try {
      const response = await api.get(`/api/ticket-generation/status/${bookingId}`);
      
      if (response.data.success) {
        setBooking(response.data.booking);
        setCloudinaryStatus(response.data.cloudinaryStatus);
        
        // إعادة الفحص إذا كانت العملية قيد التنفيذ
        if (response.data.booking.paymentStatus === 'completed' && 
            !response.data.cloudinaryStatus.generated) {
          setTimeout(() => checkStatus(), 5000);
        }
      } else {
        setError(response.data.message || 'خطأ في جلب حالة التذاكر');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'خطأ في الاتصال بالخادم');
    } finally {
      setLoading(false);
    }
  };

  const getProgress = () => {
    if (!booking || !cloudinaryStatus) return 0;
    let progress = 0;
    if (booking.paymentStatus === 'completed') progress += 33;
    if (booking.ticketsCount > 0) progress += 33;
    if (cloudinaryStatus.generated) progress += 34;
    return progress;
  };

  return (
    <motion.div 
      className="check-status-page"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <div className="container">
        <h1>🎫 متابعة حالة التذاكر</h1>
        
        <div className="search-box">
          <input 
            type="text" 
            value={bookingId}
            onChange={(e) => setBookingId(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && checkStatus()}
            placeholder="أدخل معرف الحجز (Booking ID)"
          />
          <button onClick={checkStatus} disabled={loading}>
            {loading ? 'جاري الفحص...' : 'فحص الحالة'}
          </button>
        </div>

        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        {loading && (
          <div className="loading">
            <div className="spinner"></div>
            <p>جاري فحص الحالة...</p>
          </div>
        )}

        {booking && cloudinaryStatus && (
          <motion.div 
            className="status-container"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h2>حالة التذاكر</h2>
            
            <div className="status-item">
              <span className="status-label">رقم الحجز:</span>
              <span className="status-value">{booking.bookingCode}</span>
            </div>
            
            <div className="status-item">
              <span className="status-label">حالة الدفع:</span>
              <span className="status-value">
                <span>{booking.paymentStatus === 'completed' ? 'مكتمل' : 'قيد الانتظار'}</span>
                <span className={`status-icon ${booking.paymentStatus === 'completed' ? 'success' : 'pending'}`}>
                  {booking.paymentStatus === 'completed' ? '✅' : '⏳'}
                </span>
              </span>
            </div>
            
            <div className="status-item">
              <span className="status-label">عدد التذاكر:</span>
              <span className="status-value">{booking.ticketsCount}</span>
            </div>
            
            <div className="status-item">
              <span className="status-label">حالة إنشاء PDF:</span>
              <span className="status-value">
                <span>{booking.ticketsCount > 0 ? 'تم الإنشاء' : 'لم يتم الإنشاء'}</span>
                <span className={`status-icon ${booking.ticketsCount > 0 ? 'success' : 'error'}`}>
                  {booking.ticketsCount > 0 ? '✅' : '❌'}
                </span>
              </span>
            </div>
            
            <div className="status-item">
              <span className="status-label">حالة رفع Cloudinary:</span>
              <span className="status-value">
                <span>
                  {cloudinaryStatus.generated && cloudinaryStatus.uploadedCount > 0
                    ? `تم رفع ${cloudinaryStatus.uploadedCount} ملف`
                    : booking.paymentStatus === 'completed'
                    ? 'جاري الرفع...'
                    : 'في انتظار الدفع'}
                </span>
                <span className={`status-icon ${
                  cloudinaryStatus.generated ? 'success' : 
                  booking.paymentStatus === 'completed' ? 'pending' : 'info'
                }`}>
                  {cloudinaryStatus.generated ? '☁️✅' : 
                   booking.paymentStatus === 'completed' ? '⏳' : '⏸️'}
                </span>
              </span>
            </div>
            
            <div className="status-item">
              <span className="status-label">البريد الإلكتروني:</span>
              <span className="status-value">{booking.email}</span>
            </div>
            
            <div className="progress-bar">
              <div 
                className="progress-fill" 
                style={{ width: `${getProgress()}%` }}
              >
                {getProgress()}%
              </div>
            </div>
            
            {cloudinaryStatus.generated && booking.ticketUrls && booking.ticketUrls.length > 0 && (
              <div className="download-section">
                <h3>✅ التذاكر جاهزة للتحميل!</h3>
                <p>تم إرسال روابط التحميل إلى بريدك الإلكتروني</p>
                
                <div className="download-buttons">
                  {booking.ticketsZipUrl && (
                    <button 
                      className="download-btn"
                      onClick={() => window.open(booking.ticketsZipUrl, '_blank')}
                    >
                      📦 تحميل جميع التذاكر (ZIP)
                    </button>
                  )}
                  
                  {booking.ticketUrls.slice(0, 5).map((ticket) => (
                    <button 
                      key={ticket.ticketNumber}
                      className="download-btn"
                      onClick={() => window.open(ticket.downloadUrl, '_blank')}
                    >
                      🎫 تذكرة {ticket.ticketNumber}
                    </button>
                  ))}
                  
                  {booking.ticketUrls.length > 5 && (
                    <p className="more-tickets">
                      وعدد {booking.ticketUrls.length - 5} تذكرة أخرى...
                    </p>
                  )}
                </div>
              </div>
            )}
          </motion.div>
        )}
      </div>
    </motion.div>
  );
}

export default CheckTicketStatus;